import moment from 'moment';
import { useContext, useMemo } from 'react';
import {
  PropertyStatusesEnum,
  DashFeaturesEnum,
  DATE_MONTH_FORMAT,
  formatPriceRange,
  formatAddress,
  ProjectStatusesEnum,
} from 'dash-common';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Flex,
  Thumbnail,
  Text,
  Icon,
  DropDown,
  TooltipWrapper,
  ModalContext,
  useServerResponseTranslation,
  toast,
  Badge,
  useCheckIfSubscriptionFeatureActiveBroker,
} from 'dash-ui';

import {
  GetPropertyVisitActivitiesDocument,
  PropertyVisitActivityFragment,
  useDeleteBrokerActivityByIdMutation,
  useGetPortfolioFolderRootQuery,
} from 'gql/generated.graphql';
import { RoutesPath } from 'flow-components/routes/routes';

import {
  ThumbnailWrapper,
  LockIconBox,
  PropertyItemWrapper,
  PropertyItemDetailsContainer,
  PropertyItemDetailsMobileContainer,
} from './PropertyItem.styles';

const PropertyItemDetails = ({
  visitedProperty,
}: {
  visitedProperty: PropertyVisitActivityFragment;
}) => {
  const theme = useTheme();
  const { t } = useTranslation(['common', 'pages']);
  const { property, createdAt } = visitedProperty;
  const formattedAddress = useMemo(
    () => formatAddress(property?.location.address),
    [property?.location.address]
  );
  return (
    <>
      <TooltipWrapper content={formattedAddress}>
        <Flex w="100%">
          <Flex minWidth="fit-content">
            <Text>
              <Text color={theme.colors.n70} as="span">
                {t('pages:dashboardPage.viewedProperties.viewed', {
                  date: moment(createdAt).format(DATE_MONTH_FORMAT),
                })}
              </Text>
              <Text color={theme.colors.n70} as="span">
                &nbsp;&middot;&nbsp;
              </Text>
            </Text>
          </Flex>
          <Flex w="inherit" overflow="hidden">
            <Text ellipsis={true} color={theme.colors.n70}>
              {formattedAddress}
            </Text>
          </Flex>
        </Flex>
      </TooltipWrapper>
      <Flex>
        {property.projectStatus === ProjectStatusesEnum.COMING_SOON ? (
          <Badge
            title={'Coming Soon'}
            bgColor={theme.colors.setOpacity(theme.colors.b30, 70)}
            titleColor={theme.colors.b50}
            mr={3}
            square={true}
          />
        ) : (
          <Text semibold ellipsis>
            {formatPriceRange(
              property.priceRange?.min || 0,
              property.priceRange?.max || 0,
              {
                shortMin: false,
                shortMax: false,
                prefix: t('common:labels:from') || '',
              },
              {
                maximumFractionDigits: 2,
              }
            ) || t('common:labels:noValue')}
          </Text>
        )}
      </Flex>
    </>
  );
};

const PropertyItem = ({
  visitedProperty,
}: {
  visitedProperty: PropertyVisitActivityFragment;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'pages']);
  const { toggleModal, setPayload } = useContext(ModalContext);
  const { property } = visitedProperty;
  const [deleteActivity] = useDeleteBrokerActivityByIdMutation({
    refetchQueries: [GetPropertyVisitActivitiesDocument],
  });
  const translateServerMessages = useServerResponseTranslation();
  const isPropertyLocked = [
    PropertyStatusesEnum.PRIVATE,
    PropertyStatusesEnum.DRAFT,
  ].includes(property.status as PropertyStatusesEnum);
  const { checkFeatureAccess } = useCheckIfSubscriptionFeatureActiveBroker([
    DashFeaturesEnum.PORTFOLIO_CREATION_AND_SHARING,
  ]);

  const file = property.mainPropertyMediaFile?.files[0];

  return (
    <PropertyItemWrapper
      w="100%"
      p={3}
      mb={2}
      flexDirection="column"
      onClick={() => {
        navigate(RoutesPath.PropertyPreview.replace(':id', property._id));
      }}
    >
      <Flex w="100%" justifyContent="space-between">
        <Flex>
          <ThumbnailWrapper w={80} h={80} overflow="hidden" mr={4}>
            <Thumbnail file={file} />
          </ThumbnailWrapper>
        </Flex>
        <Flex overflow="auto" flexDirection="column" flex={2}>
          <Box w="100%">
            <Flex alignItems="center">
              <Flex m={1}>
                <Text semibold>{property.name}</Text>
              </Flex>
              {isPropertyLocked && (
                <TooltipWrapper
                  content={t(
                    'pages:dashboardPage.viewedProperties.unavailableProperty'
                  )}
                >
                  <LockIconBox
                    data-tooltip-id="private-status"
                    ml={2}
                    w={32}
                    h={24}
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Icon name="lock" size={14} color={theme.colors.n80} />
                  </LockIconBox>
                </TooltipWrapper>
              )}
            </Flex>
            <PropertyItemDetailsContainer>
              <PropertyItemDetails visitedProperty={visitedProperty} />
            </PropertyItemDetailsContainer>
          </Box>
        </Flex>
        <Box>
          <DropDown
            placementHorizontal="left"
            variant="dark"
            Anchor={() => <Icon mt={2} name="dots" size={15} />}
            items={[
              {
                children: t('pages:propertyPage.addToPortfolio'),
                w: 181,
                key: 'add',
                contentCentered: false,
                disabled: isPropertyLocked,
                onClick: () => {
                  if (checkFeatureAccess()) {
                    setPayload({ propertyId: property._id });
                    toggleModal(true, 'AddPropertyToPortfolio');
                  }
                },
              },
              {
                children: t('common:labels.clear'),
                w: 181,
                key: 'clear',
                contentCentered: false,
                onClick: () => {
                  deleteActivity({
                    variables: {
                      activityId: visitedProperty._id,
                    },
                    onError: (error) => {
                      toast.error(translateServerMessages(error));
                    },
                  });
                },
              },
            ]}
          />
        </Box>
      </Flex>
      <PropertyItemDetailsMobileContainer w="100%" flexDirection="column">
        <PropertyItemDetails visitedProperty={visitedProperty} />
      </PropertyItemDetailsMobileContainer>
    </PropertyItemWrapper>
  );
};

export default PropertyItem;
